import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../utils/api";

// Rapport de triage POK (23/09/2026) — calqué sur RapportTriage.jsx (Syrinix, P1-C), avec les
// simplifications actées le 11/09/2026 (docs/tasks/PLAN_DE_TRAVAIL.md) : une seule source
// automatique côté POK (l'appareil, mesure directe) donc 2 colonnes Q1 (Q1op/Q1pok) au lieu de 4,
// Cv-op-pok remplace Cvhe, groupement par campagne au lieu de site_id. Pas de section "invalides"
// ni "sans association" séparée : le bucket N/A (campagne_id="NA") réutilise le même tableau.

const Q1_COLUMNS = [
  { key: "q1_operateur", label: "Q1op", title: "Q1 opérateur — débit mesuré/saisi par l'opérateur (Kizeo)" },
  { key: "q1_pok", label: "Q1pok", title: "Q1 POK — débit mesuré directement par l'appareil" },
];

const CV_TITLE = "Cv-op-pok — écart relatif entre Q1 opérateur et Q1 POK : (Q1op − Q1pok) / Q1op";

const DISAGREEMENT_KEYS = ["q1_operateur", "q1_pok"];

// Q1pok (mesure directe de l'appareil) prime sur Q1op pour le regroupement par défaut.
const CATEGORY_RANK_ORDER = ["q1_pok", "q1_operateur"];

function categoryOf(q1) {
  if (q1 === null || q1 === undefined) return null;
  if (q1 < 30) return "rouge";
  if (q1 < 60) return "orange";
  return "vert";
}

const CATEGORY_CLASS = {
  rouge: "bg-red-100 text-red-800",
  orange: "bg-orange-100 text-orange-800",
  vert: "bg-green-100 text-green-800",
};

function fmtQ1(v) {
  return v === null || v === undefined ? "-" : String(Math.round(v));
}

function fmtCv(v) {
  if (v === null || v === undefined) return "-";
  const pct = (v * 100).toFixed(1);
  return v > 0 ? `+${pct}%` : `${pct}%`;
}

function disagrees(row) {
  const cats = DISAGREEMENT_KEYS.map((k) => categoryOf(row[k])).filter((c) => c !== null);
  if (cats.length < 2) return false;
  return new Set(cats).size > 1;
}

function categoryRank(row) {
  for (const k of CATEGORY_RANK_ORDER) {
    const c = categoryOf(row[k]);
    if (c) return c === "rouge" ? 0 : c === "orange" ? 1 : 2;
  }
  return 3;
}

function insee5(piId) {
  return (piId || "").split("_")[0] || "";
}

export default function RapportTriagePok() {
  const [searchParams] = useSearchParams();
  const campagneId = searchParams.get("campagne_id") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [insee, setInsee] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  useEffect(() => {
    if (!campagneId) {
      setError("Aucun campagne_id fourni dans l'URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    apiFetch(`/api/pok/report/triage/${campagneId}/table`)
      .then(setData)
      .catch((e) => setError(e.message || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [campagneId]);

  const rows = data?.rows || [];

  const filtered = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (searchLower && !(r.pi_id || "").toLowerCase().includes(searchLower)) return false;
      if (insee && insee5(r.pi_id) !== insee) return false;
      return true;
    });
  }, [rows, search, insee]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sort.key) {
      arr.sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
        if (bv === null || bv === undefined) return -1;
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      });
    } else {
      // Regroupement par défaut : désaccords d'abord, puis 0-29, 30-59, 60+.
      arr.sort((a, b) => {
        const da = disagrees(a) ? 0 : 1;
        const db = disagrees(b) ? 0 : 1;
        if (da !== db) return da - db;
        return categoryRank(a) - categoryRank(b);
      });
    }
    return arr;
  }, [filtered, sort]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" }; // 3e clic -> retour au regroupement par défaut
    });
  };

  const sortIndicator = (key) => {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  };

  const openStreamlit = (row) => {
    const url = new URL("/visualisation/POK", window.location.origin);
    url.searchParams.set("date", row.date);
    url.searchParams.set("essai_folder", row.essai_folder);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-xl font-semibold mb-1">Rapport de triage POK — {data?.campagne_nom || campagneId}</h1>
      <p className="text-sm text-gray-500 mb-4">
        {rows.length} essai{rows.length !== 1 ? "s" : ""}
      </p>

      {loading && <p className="text-gray-500">Chargement…</p>}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <>
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              type="text"
              placeholder="Rechercher un N° PI"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded w-56"
            />
            <input
              type="text"
              placeholder="Filtrer par code INSEE"
              value={insee}
              onChange={(e) => setInsee(e.target.value.replace(/\D/g, "").slice(0, 5))}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded w-48"
            />
            {(search || insee) && (
              <button
                type="button"
                onClick={() => { setSearch(""); setInsee(""); }}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
              >
                Réinitialiser
              </button>
            )}
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <Th onClick={() => toggleSort("pi_id")}>N° PI{sortIndicator("pi_id")}</Th>
                  <Th onClick={() => toggleSort("timestamp_start")}>Date{sortIndicator("timestamp_start")}</Th>
                  {Q1_COLUMNS.map((c) => (
                    <Th key={c.key} title={c.title} onClick={() => toggleSort(c.key)}>
                      {c.label}{sortIndicator(c.key)}
                    </Th>
                  ))}
                  <Th title={CV_TITLE} onClick={() => toggleSort("cv_op_pok")}>Cv-op-pok{sortIndicator("cv_op_pok")}</Th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Ouvrir</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr
                    key={r.essai_id}
                    className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 ${
                      disagrees(r) ? "ring-1 ring-inset ring-amber-300" : ""
                    }`}
                  >
                    <td className="px-3 py-1.5 font-mono">{r.pi_id || "-"}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.timestamp_start || "-"}</td>
                    {Q1_COLUMNS.map((c) => {
                      const cat = categoryOf(r[c.key]);
                      return (
                        <td key={c.key} className="px-3 py-1.5">
                          <span className={`inline-block px-2 py-0.5 rounded ${cat ? CATEGORY_CLASS[cat] : "text-gray-400"}`}>
                            {fmtQ1(r[c.key])}
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-3 py-1.5">{fmtCv(r.cv_op_pok)}</td>
                    <td className="px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => openStreamlit(r)}
                        className="px-2 py-0.5 text-xs rounded border border-violet-500 text-violet-600 hover:bg-violet-50"
                      >
                        Ouvrir
                      </button>
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-gray-400">
                      Aucun essai ne correspond aux filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Th({ onClick, title, children }) {
  return (
    <th
      onClick={onClick}
      title={title}
      className="px-3 py-2 text-left font-medium text-gray-600 cursor-pointer select-none hover:text-gray-900 whitespace-nowrap"
    >
      {children}
    </th>
  );
}
